import React, { useState, useContext, useEffect, useRef } from "react";
import {
  Box,
  Button,
  Grid,
  Modal,
  Divider,
  Tooltip,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import CreateTaskForm from "./CreateTaskForm";
import ApiEndpoints from "../../network/ApiEndPoints";
import ApiPaginateSearch from "../../component/ApiPaginateSearch";
import { dateToTime1, ddmmyy } from "../../utils/DateUtils";
import AuthContext from "../../store/AuthContext";
import { CustomStyles } from "../../component/CustomStyle";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ChangeDeveloper from "../../component/ChangeDeveloper";
import ResignTask from "./ResignTask";
import EditTask from "./EditTask";
import DeleteTask from "./DeleteTask";
import ViewModal from "../../modals/ViewModal";
import MerchantApproval from "../../component/MerchantApproval";
import FilterModal from "../../modals/FilterModal";
import DataFilter from "./Common/DataFilter";

const TaskBoard = ({ taskData }) => {
  const [apiData, setApiData] = useState([]);
  const authCtx = useContext(AuthContext);
  const user = authCtx?.user;
  const [activeFilterButton, setActiveFilterButton] = useState("");
  const [filterParams, setFilterParams] = useState("");
  const [advancedFilterParams, setAdvancedFilterParams] = useState("");
  const [combinedQuery, setCombinedQuery] = useState("");
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [openDescModal, setOpenDescModal] = useState(false);
  const [selectedDescription, setSelectedDescription] = useState("");

  let refresh;

  // Combine filters for API query
  useEffect(() => {
    let queryParts = [];

    // Only add filterParams if there's no advanced filter or if it's not conflicting
    if (filterParams && !advancedFilterParams) {
      queryParts.push(filterParams);
    } else if (filterParams && advancedFilterParams) {
      // Check if advanced filter already has status parameter to avoid conflicts
      if (!advancedFilterParams.includes('status=')) {
        queryParts.push(filterParams);
      }
      // Add advanced filter params
      queryParts.push(advancedFilterParams);
    } else if (advancedFilterParams) {
      queryParts.push(advancedFilterParams);
    }

    const combined = queryParts.join("&");
    setCombinedQuery(combined);

    console.log("Filter Params:", filterParams);
    console.log("Advanced Params:", advancedFilterParams);
    console.log("Combined Query:", combined);
  }, [filterParams, advancedFilterParams]);

  // Initialize based on user role
  useEffect(() => {
    if (user.role === "Backend" || user.role === "Developer") {
      setFilterParams(`assigned_to=${user.id}`);
      setActiveFilterButton("assigned");
    } else if (user.role === "Merchant") {
      setFilterParams("");
      setActiveFilterButton("all");
    } else {
      setFilterParams("");
      setActiveFilterButton("all");
    }
  }, [user.role, user.id]);

  // Function to determine which button should be active based on advanced filters
  const getActiveButtonFromAdvancedFilters = (params) => {
    if (!params) return "";
    
    if (params.includes('status=unassigned')) {
      return "unassigned";
    } else if (params.includes('status=assigned') || params.includes('assigned_to=')) {
      return "assigned";
    } else if (params.includes('status=open')) {
      return "pending";
    } else if (params.includes('status=COMPLETED')) {
      return "completed";
    }
    return "";
  };

  const handleButtonFilter = (filter, buttonName) => {
    setFilterParams(filter);
    setActiveFilterButton(buttonName);
    // Clear advanced filters when using button filters
    setAdvancedFilterParams("");
    
    // Log when unassigned filter is applied (for debugging)
    if (buttonName === "unassigned") {
      console.log("Unassigned filter applied");
    }
  };

  const handleAdvancedFilter = (params) => {
    setAdvancedFilterParams(params);
    
    // Determine which button should be active based on the advanced filter params
    const activeButton = getActiveButtonFromAdvancedFilters(params);
    
    if (activeButton) {
      setActiveFilterButton(activeButton);
      // Don't set filterParams when using advanced filters to avoid duplication
      setFilterParams("");
    } else {
      // If no matching button, clear the button state
      setActiveFilterButton("");
      setFilterParams("");
    }
  };

  const resetToDefaultState = () => {
    // Reset to default based on user role
    if (user.role === "Backend" || user.role === "Developer") {
      setFilterParams(`assigned_to=${user.id}`);
      setActiveFilterButton("assigned");
    } else if (user.role === "Merchant") {
      setFilterParams("");
      setActiveFilterButton("all");
    } else {
      setFilterParams("");
      setActiveFilterButton("all");
    }
    setAdvancedFilterParams("");
  };

  // This function will be called by the DataFilter component when its reset button is clicked
  const handleDataFilterReset = () => {
    resetToDefaultState();
  };

  const refreshFunc = () => {
    if (refresh) refresh();
  };

  const columns = [
    {
      name: "Date",
      center: true,
      selector: (row) => (
        <div style={{ textAlign: "center" }}>
          <Tooltip title="Start Date">
            <div>
              <span>{ddmmyy(row.start_date)}</span>
              <span style={{ marginLeft: "6px" }}>
                {dateToTime1(row.created_at)}
              </span>
            </div>
          </Tooltip>

          <Tooltip title="End Date">
            <div style={{ marginBottom: "0.3em" }}>
              {ddmmyy(row.end_date)}
              <span style={{ marginLeft: "6px" }}>
                {dateToTime1(row.updated_at)}
              </span>
            </div>
          </Tooltip>
        </div>
      ),
    },
    {
      name: "Task ID",
      width: "120px",
      wrap: false,
      center: true,
      selector: (row) => <div style={{ textAlign: "center" }}>{row.id}</div>,
    },
    {
      name: "Project Name",
      width: "140px",
      wrap: false,
      center: true,
      selector: (row) => (
        <div style={{ textAlign: "center" }}>{row.project_name}</div>
      ),
    },
    {
      name: "Category",
      selector: (row) => (
        <div style={{ textAlign: "center" }}>{row.category}</div>
      ),
      wrap: true,
    },
    user.role === "Admin" && {
      name: "Assigned To",
      selector: (row) => (
        <>
          <div>
            {row.assigned_name ? (
              row.assigned_name
            ) : (
              <span style={{ color: "red" }}>Not Assigned</span>
            )}
          </div>

          {user.role === "Admin" && (
            <ChangeDeveloper refresh={refresh} row={row} />
          )}
        </>
      ),
    },

    {
      name: "Developer Remarks",
      selector: (row) => {
        return (
          <div
            style={{
              maxHeight: "80px",
              overflowY: "auto",
              padding: "10px",
              position: "relative",
              borderRadius: "8px",
              background: "#f9f9f9",
              scrollbarWidth: "thin",
              textAlign: "center",
            }}
          >
            <div
              style={{ marginBottom: "12px", color: "#333", fontSize: "14px" }}
            >
              {row.developer_remark || "NA"}
            </div>
          </div>
        );
      },
      wrap: true,
      width: "240px",
    },
    {
      name: "Description",
      width: "240px",
      minWidth: "240px",
      maxWidth: "240px",
      wrap: true,
      selector: (row) => (
        <div
          onClick={() => {
            setSelectedDescription(row.description);
            setOpenDescModal(true);
          }}
          style={{
            width: "220px",
            maxWidth: "220px",
            maxHeight: "80px",
            overflowY: "auto",
            overflowX: "hidden",
            padding: "10px",
            position: "relative",
            borderRadius: "8px",
            background: "#f9f9f9",
            boxShadow: "0px 2px 5px rgba(0, 0, 0, 0.1)",
            cursor: "pointer",
            textAlign: "center",
            wordBreak: "break-word",
            whiteSpace: "normal",
          }}
        >
          <div
            style={{ marginBottom: "12px", color: "#333", fontSize: "14px" }}
          >
            {row.description}
          </div>
        </div>
      ),
    },
    {
      name: "Merchant Remark",
      selector: (row) => (
        <div
          onClick={() => {
            setSelectedDescription(row.remark);
            setOpenDescModal(true);
          }}
          style={{
            maxHeight: "80px",
            overflowY: "auto",
            padding: "10px",
            position: "relative",
            borderRadius: "8px",
            background: "#f9f9f9",
            boxShadow: "0px 2px 5px rgba(0, 0, 0, 0.1)",
            cursor: "pointer",
            textAlign: "center",
          }}
        >
          <div
            style={{ marginBottom: "12px", color: "#333", fontSize: "14px" }}
          >
            {row.remark || "NA"}
          </div>
        </div>
      ),
      wrap: true,
      width: "240px",
    },
    ...(user.role === "Admin"
      ? [
          {
            name: "Weightage",
            selector: (row) => <div>{row.weightage}</div>,
            wrap: true,
            width: "100px",
          },
        ]
      : []),
    ...(user.role === "Admin"
      ? [
          {
            name: "Task Type",
            selector: (row) => (
              <div>{row?.task_type?.toUpperCase() || "MAINTENANCE"}</div>
            ),
            wrap: true,
            width: "100px",
          },
        ]
      : []),
    ...(user.role === "Admin"
      ? [
          {
            name: "Amount",
            selector: (row) => <div>{row?.amount || "0"}</div>,
            wrap: true,
            width: "100px",
          },
        ]
      : []),

    (user.role === "Admin" || user.role === "Developer") && {
      name: "Created By",
      selector: (row) => <div>{row.created_by_name}</div>,
      wrap: true,
      width: "140px",
    },

    {
      name: "Priority",
      selector: (row) => (
        <div
          style={{
            fontWeight: "bold",
            fontSize: "15px",
            textTransform: "uppercase",
            color:
              row.priority === "High"
                ? "rgb(200, 0, 0)"
                : row.priority === "Medium"
                ? "rgb(229, 127, 10)"
                : "rgb(34, 139, 34)",
          }}
        >
          {row.priority}
        </div>
      ),
    },
    {
      name: "Status",
      selector: (row) => (
        <Box
          sx={{
            fontWeight: "bold",
            fontSize: "12px",
            textTransform: "uppercase",
            letterSpacing: "0.5px",
            color:
              row.status === "INPROGRESS" ||
              row.status === "COMPLETED" ||
              row.status === "MAINTENANCE" ||
              row.status === "OPEN" ||
              row.status === "REJECTED"
                ? "#fff"
                : "#000",
            backgroundColor:
              row.status === "INPROGRESS"
                ? "#C76E15"
                : row.status === "COMPLETED"
                ? "#0AA80A"
                : row.status === "MAINTENANCE"
                ? "#0073E6"
                : row.status === "OPEN"
                ? "rgb(200, 126, 73)"
                : row.status === "REJECTED"
                ? "#FF0000"
                : "#D4C900",
            padding: "6px 10px",
            textAlign: "center",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            minWidth: "fit-content",
            width: "auto",
            boxShadow: "none",
            transition: "all 0.3s ease",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            "&:hover": {
              backgroundColor:
                row.status === "INPROGRESS"
                  ? "#C76E15"
                  : row.status === "COMPLETED"
                  ? "#0AA80A"
                  : row.status === "MAINTENANCE"
                  ? "#0073E6"
                  : row.status === "OPEN"
                  ? "rgb(200, 126, 73)"
                  : row.status === "REJECTED"
                  ? "#FF0000"
                  : "#D4C900",
              color: "#fff",
              transform: "scale(1.05)",
            },
          }}
        >
          {row.status}
        </Box>
      ),
    },
    {
      name: "Image",
      selector: (row) => (
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          {row?.task_img === null ? (
            <VisibilityIcon sx={{ color: "grey" }} />
          ) : (
            <ViewModal row={row} title="Task Image" />
          )}
        </div>
      ),
    },
    {
      name: "Actions",
      center: true,
      selector: (row) => {
        const isMerchant = user.role === "Merchant";
        const isPaidTask = row.task_type === "paid";
        const merchantResponse = row.merchant_response;

        return (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              gap: 6,
              alignItems: "center",
            }}
          >
            {isMerchant && isPaidTask && merchantResponse === "pending" && (
              <MerchantApproval row={row} refresh={refresh} />
            )}

            {(!isMerchant || !isPaidTask || merchantResponse !== "pending") && (
              <>
                {(filterParams.includes("status=unassigned") || advancedFilterParams.includes("status=unassigned")) &&
                  user.role !== "Admin" &&
                  row.status !== "COMPLETED" && (
                    <ResignTask data={row} refresh={refresh} row={row} />
                  )}
                <EditTask data={row} refresh={refresh} row={row} />
                <DeleteTask
                  data={row}
                  getTasks={refresh}
                  row={row}
                  disabled={user?.role !== "Admin"}
                />
              </>
            )}
          </div>
        );
      },
      width: user.role === "Merchant" ? "200px" : "100px",
    },
  ].filter(Boolean);

  const renderFilterButtons = () => {
    if (user.role === "Merchant") {
      return (
        <>
          <Button
            variant={activeFilterButton === "all" ? "contained" : "outlined"}
            sx={{
              backgroundColor:
                activeFilterButton === "all" ? "#2F3B57" : "inherit",
              color: activeFilterButton === "all" ? "white" : "#2F3B57",
              borderColor: "#2F3B57",
              "&:hover": {
                backgroundColor:
                  activeFilterButton === "all"
                    ? "#1a2536"
                    : "rgba(47, 59, 87, 0.1)",
              },
            }}
            onClick={() => handleButtonFilter("", "all")}
          >
            All
          </Button>
          <Button
            variant={
              activeFilterButton === "pending" ? "contained" : "outlined"
            }
            sx={{
              backgroundColor:
                activeFilterButton === "pending" ? "#2F3B57" : "inherit",
              color: activeFilterButton === "pending" ? "white" : "#2F3B57",
              borderColor: "#2F3B57",
              "&:hover": {
                backgroundColor:
                  activeFilterButton === "pending"
                    ? "#1a2536"
                    : "rgba(47, 59, 87, 0.1)",
              },
            }}
            onClick={() => handleButtonFilter("status=open", "pending")}
          >
            Pending
          </Button>
          <Button
            variant={
              activeFilterButton === "completed" ? "contained" : "outlined"
            }
            sx={{
              backgroundColor:
                activeFilterButton === "completed" ? "#2F3B57" : "inherit",
              color: activeFilterButton === "completed" ? "white" : "#2F3B57",
              borderColor: "#2F3B57",
              "&:hover": {
                backgroundColor:
                  activeFilterButton === "completed"
                    ? "#1a2536"
                    : "rgba(47, 59, 87, 0.1)",
              },
            }}
            onClick={() => handleButtonFilter("status=COMPLETED", "completed")}
          >
            Completed
          </Button>
        </>
      );
    }

    // For Admin, Developer, Backend
    return (
      <>
        {/* All Button */}
        <Button
          variant={activeFilterButton === "all" ? "contained" : "outlined"}
          sx={{
            backgroundColor:
              activeFilterButton === "all" ? "#2F3B57" : "inherit",
            color: activeFilterButton === "all" ? "white" : "#2F3B57",
            borderColor: "#2F3B57",
            "&:hover": {
              backgroundColor:
                activeFilterButton === "all"
                  ? "#1a2536"
                  : "rgba(47, 59, 87, 0.1)",
            },
          }}
          onClick={() => {
            handleButtonFilter("", "all");
          }}
        >
          All
        </Button>

        {(user.role === "Developer" || user.role === "Backend") && (
          <Button
            variant={
              activeFilterButton === "assigned" ? "contained" : "outlined"
            }
            sx={{
              backgroundColor:
                activeFilterButton === "assigned" ? "#2F3B57" : "inherit",
              color: activeFilterButton === "assigned" ? "white" : "#2F3B57",
              borderColor: "#2F3B57",
              "&:hover": {
                backgroundColor:
                  activeFilterButton === "assigned"
                    ? "#1a2536"
                    : "rgba(47, 59, 87, 0.1)",
              },
            }}
            onClick={() => {
              if (user.role === "Admin") {
                handleButtonFilter("status=assigned", "assigned");
              } else {
                handleButtonFilter(`assigned_to=${user.id}`, "assigned");
              }
            }}
          >
            Assigned
          </Button>
        )}

        {(user.role === "Admin" || user.role === "Developer") && (
          <Button
            variant={
              activeFilterButton === "unassigned" ? "contained" : "outlined"
            }
            sx={{
              backgroundColor:
                activeFilterButton === "unassigned" ? "#2F3B57" : "inherit",
              color: activeFilterButton === "unassigned" ? "white" : "#2F3B57",
              borderColor: "#2F3B57",
              "&:hover": {
                backgroundColor:
                  activeFilterButton === "unassigned"
                    ? "#1a2536"
                    : "rgba(47, 59, 87, 0.1)",
              },
            }}
            onClick={() =>
              handleButtonFilter("status=unassigned", "unassigned")
            }
          >
            Unassigned
          </Button>
        )}
      </>
    );
  };

  return (
    <>
      <Grid sx={{ mt: 4 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            flexWrap: "wrap",
            gap: 2,
            mb: 2,
          }}
        >
          {/* Filter Buttons */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              flexWrap: "wrap",
            }}
          >
            {renderFilterButtons()}
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <CreateTaskForm refresh={refreshFunc} />
          </Box>
        </Box>

        {/* Advanced Filters */}
        {isSmallScreen ? (
          <FilterModal
            ifDateFilter
            ifStatusFilter
            ifProjectNameFilter
            ifPriorityFilter
            ifCategoryFilter
            ifAssignedToFilter={user.role === "Admin"}
            ifCreatedByFilter
            setQuary={handleAdvancedFilter}
            setQuery={handleAdvancedFilter}
            queryParam={combinedQuery}
            unassigned={filterParams.includes("status=unassigned") }
            handleRefresh={refreshFunc}
           
          />
        ) : (
          <DataFilter
            ifDateFilter
            ifstatusFilter3
            ifProjectNameFilter
            ifPriorityFilter
            ifCategoryFilter
            ifAssignedToFilter={user.role === "Admin"}
            ifCreatedByFilter
            setQuary={handleAdvancedFilter}
            queryParam={combinedQuery}
            unassigned={filterParams.includes("status=unassigned") }
              onReset={handleDataFilterReset}
          
          />
        )}

        {/* Task Table */}
        <ApiPaginateSearch
          apiEnd={ApiEndpoints.GET_TASK}
          columns={columns}
          apiData={apiData}
          setApiData={setApiData}
          tableStyle={CustomStyles}
          queryParam={combinedQuery}
          returnRefetch={(ref) => {
            refresh = ref;
          }}
        />
      </Grid>

      {/* Description Modal */}
      <Modal open={openDescModal} onClose={() => setOpenDescModal(false)}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "90%", sm: 400 },
            bgcolor: "background.paper",
            borderRadius: 2,
            boxShadow: 24,
            p: 3,
          }}
        >
          <Box sx={{ fontWeight: "bold", mb: 2 }}>Task Description</Box>
          <Divider sx={{ mb: 2 }} />
          <Box sx={{ maxHeight: "300px", overflowY: "auto" }}>
            {selectedDescription}
          </Box>
          <Box sx={{ textAlign: "right", mt: 2 }}>
            <Button variant="contained" onClick={() => setOpenDescModal(false)}>
              Close
            </Button>
          </Box>
        </Box>
      </Modal>
    </>
  );
};

export default TaskBoard;